import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemButton,
  ListItemIcon,
  Drawer,
  Divider,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Pagination,
  useTheme,
  useMediaQuery,
  TableSortLabel,
  ThemeProvider,
  ListSubheader,
  LinearProgress,
  Tooltip,
  createTheme,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  Search,
  People,
  Business,
  TrendingUp,
  Warning,
  Delete,
  Edit,
  Star,
  Event,
  Person as PersonIcon,
  AttachMoney,
  Receipt,
  Article,
  EmojiEvents,
  Email,
  Menu as MenuIcon,
  OpenInNew,
  Block,
  BugReport as BugReportIcon,
  Forum,
  Gavel,
  PersonAdd,
  UploadFile,
  MarkEmailRead,
  Restore,
  ExpandMore,
  Close,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import adminService from '../../services/adminService';
import DialogHeader from '../../components/Common/DialogHeader';
import commissionService from '../../services/commissionService';

// ÚJ: Komponensek az új tabokhoz
import CommissionGroupsPage from '../../components/Admin/CommissionGroupsPage';
import CommissionedBookingsPage from '../../components/Admin/CommissionedBookingsPage';
import AdminBlogPostsPage from '../../components/Admin/AdminBlogPostsPage';
import AdminFeaturedPage from '../../components/Admin/AdminFeaturedPage';
import AdminInvoicesPage from '../../components/Admin/AdminInvoicesPage';
import AdminBulkEmailPage from '../../components/Admin/AdminBulkEmailPage';
import BugReportsPage from '../../components/Admin/BugReportsPage';
import AdminLegalPage from '../../components/Admin/AdminLegalPage';
import AdminCategoryContentPage from '../../components/Admin/AdminCategoryContentPage';
import AdminDraftProvidersPage from '../../components/Admin/AdminDraftProvidersPage';
import AdminProviderImportPage from '../../components/Admin/AdminProviderImportPage';
import AdminEmailTemplatesPage from '../../components/Admin/AdminEmailTemplatesPage';
import RowActionsMenu from '../../components/Admin/shared/RowActionsMenu';
import MobileEntityCard from '../../components/Admin/shared/MobileEntityCard';
import ResponsiveTableContainer from '../../components/Admin/shared/ResponsiveTableContainer';
import { useConfirm } from '../../components/Common/ConfirmDialog';
import { formatHuf } from '../../utils/currency';
import { ProviderCompletenessChip, ProviderCompletenessChecklist } from '../../components/Admin/shared/ProviderCompleteness';

// Szűkebb asztali szélességen (oldalsávval) ezek a másodlagos oszlopok elrejtődnek,
// hogy a táblák vízszintes görgetés nélkül elférjenek.
const hideBelowLg = { display: { xs: 'none', lg: 'table-cell' } };
const hideBelowXl = { display: { xs: 'none', xl: 'table-cell' } };
const noWrap = { whiteSpace: 'nowrap' };

// Az alap MUI warning narancs (#ed6c02) a krém háttéren ~2.8:1, fehér szöveggel ~3:1 kontrasztú —
// az admin felületen (sok "Függőben"/"Hiányos" chip) sötétebb árnyalatot használunk (~5:1 mindkét esetben).
const withReadableWarning = (outer) => createTheme(outer, {
  palette: { warning: outer.palette.augmentColor({ color: { main: '#a64b00' }, name: 'warning' }) },
});

// A kiválasztott fül az URL-ben (?tab=...) él, így frissítés után megmarad és linkelhető
const TAB_SLUGS = {
  0: 'jovahagyasra-var',
  1: 'jovahagyott',
  2: 'felhasznalok',
  3: 'jutalekcsoportok',
  4: 'jutalekos-foglalasok',
  5: 'blog',
  6: 'featured',
  7: 'tomeges-email',
  8: 'elutasitott',
  9: 'hibabejelentesek',
  10: 'aszf',
  11: 'piszkozatok',
  12: 'torolt-szolgaltatok',
  13: 'import',
  14: 'szamlak',
  15: 'email-sablonok',
  16: 'kategoria-tartalmak',
};
const SLUG_TO_TAB = Object.fromEntries(Object.entries(TAB_SLUGS).map(([index, slug]) => [slug, Number(index)]));

const USER_TYPE_LABELS = { CLIENT: 'Ügyfél', PROVIDER: 'Szolgáltató', ADMIN: 'Admin' };

const AdminDashboard = () => {
  const { user, isAdmin, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  // lg alatt az oldalsáv helyett felső menüsáv + drawer, hogy a tábláknak a teljes szélesség jusson
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  // md alatt a szolgáltató/felhasználó táblák kártyalistaként jelennek meg
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const tabValue = SLUG_TO_TAB[searchParams.get('tab')] ?? 0;
  const [confirm, confirmDialog] = useConfirm();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  // searchInput: a mező tartalma; searchTerm: a gépelés után kis késleltetéssel (debounce) frissül,
  // hogy ne induljon minden leütésre 6 lekérés
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionResult, setActionResult] = useState(null);
  const [usersSortBy, setUsersSortBy] = useState('createdAt');
  const [usersSortOrder, setUsersSortOrder] = useState('desc');

  // Valós adatok state-jei
  const [stats, setStats] = useState(null);
  const [pendingProviders, setPendingProviders] = useState([]);
  const [pendingReadiness, setPendingReadiness] = useState('all'); // all | ready | incomplete
  const [approvedProviders, setApprovedProviders] = useState([]);
  const [rejectedProviders, setRejectedProviders] = useState([]);
  const [users, setUsers] = useState([]);
  const [deletedProviders, setDeletedProviders] = useState([]); // ÚJ: Törölt (inaktivált) szolgáltatók

  // Lapozás state-ek
  const [pendingPage, setPendingPage] = useState(1);
  const [approvedPage, setApprovedPage] = useState(1);
  const [rejectedPage, setRejectedPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [deletedPage, setDeletedPage] = useState(1); // ÚJ
  const [commissionGroupsPage, setCommissionGroupsPage] = useState(1);
  const [commissionedBookingsPage, setCommissionedBookingsPage] = useState(1);
    // ÚJ: Jutalékcsoportok és jutalékos foglalások state-jei
  const [commissionGroups, setCommissionGroups] = useState([]);
  const [commissionedBookings, setCommissionedBookings] = useState([]);


  const [pendingPagination, setPendingPagination] = useState({});
  const [approvedPagination, setApprovedPagination] = useState({});
  const [rejectedPagination, setRejectedPagination] = useState({});
  const [usersPagination, setUsersPagination] = useState({});
  const [deletedPagination, setDeletedPagination] = useState({}); // ÚJ
  const [commissionGroupsPagination, setCommissionGroupsPagination] = useState({});
  const [commissionedBookingsPagination, setCommissionedBookingsPagination] = useState({});


  const [loading, setLoading] = useState(true); // csak az első betöltésnél — utána a tartalom a helyén marad
  const [refreshing, setRefreshing] = useState(false);
  const hasLoadedRef = useRef(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteActionType, setDeleteActionType] = useState('');


  useEffect(() => {
    if (!isAuthenticated || !isAdmin()) {
      navigate('/');
    }
  }, [isAuthenticated, isAdmin, navigate]);

  const loadData = useCallback(async () => {
    if (!isAdmin()) return;

    if (hasLoadedRef.current) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const [
        statsResponse,
        pendingProvidersResponse,
        approvedProvidersResponse,
        rejectedProvidersResponse,
        usersResponse,
        deletedProvidersResponse,
      ] = await Promise.all([
        adminService.getAdminStats(),
        adminService.getPendingProviders(pendingPage, 10, searchTerm, pendingReadiness),
        adminService.getApprovedProviders(approvedPage, 10, searchTerm),
        adminService.getRejectedProviders(rejectedPage, 10, searchTerm),
        adminService.getAllUsers(usersPage, 10, searchTerm, usersSortBy, usersSortOrder),
        adminService.getDeletedProviders(deletedPage, 10, searchTerm),
      ]);

      setStats(statsResponse);
      setPendingProviders(pendingProvidersResponse.providers);
      setPendingPagination(pendingProvidersResponse.pagination);
      setApprovedProviders(approvedProvidersResponse.providers);
      setApprovedPagination(approvedProvidersResponse.pagination);
      setRejectedProviders(rejectedProvidersResponse.providers);
      setRejectedPagination(rejectedProvidersResponse.pagination);
      setUsers(usersResponse.users);
      setUsersPagination(usersResponse.pagination);
      setDeletedProviders(deletedProvidersResponse.providers);
      setDeletedPagination(deletedProvidersResponse.pagination);

    } catch (err) {
      console.error('Admin dashboard data loading error:', err);
      setError('Hiba történt az adatok betöltése során.');
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, [isAdmin, pendingPage, pendingReadiness, approvedPage, rejectedPage, usersPage, deletedPage, searchTerm, usersSortBy, usersSortOrder]);


  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleProviderAction = async (providerId, action) => {
    setActionLoading(true);
    setActionResult(null);
    try {
      const statusMap = { approve: 'APPROVED', reject: 'REJECTED', revoke: 'PENDING' };
      const status = statusMap[action];
      const response = await adminService.updateProviderStatus(providerId, status, adminNotes);

      if (response.success) {
        const actionLabel = { approve: 'jóváhagyva', reject: 'elutasítva', revoke: 'visszavonva (várakozó státuszba helyezve)' };
        setActionResult({
          type: 'success',
          message: `Szolgáltató sikeresen ${actionLabel[action]}!`
        });
        loadData(); // Adatok újratöltése
      } else {
        setActionResult({
          type: 'error',
          message: response.message || 'Hiba történt a művelet során!'
        });
      }
    } catch (error) {
      setActionResult({
        type: 'error',
        message: error.message || 'Hiba történt a művelet során!'
      });
    } finally {
      setActionLoading(false);
      setApprovalDialogOpen(false);
      setSelectedProvider(null);
      setAdminNotes('');
      setTimeout(() => setActionResult(null), 5000); // Üzenet eltüntetése
    }
  };

  // ÚJ: Törlés gombra kattintás (felhasználó és szolgáltató esetén is)
  const handleDeleteClick = (item, type) => {
    setItemToDelete(item);
    setDeleteActionType(type);
    setDeleteConfirmationDialogOpen(true);
  };

  // ÚJ: Törlés megerősítése
  const handleConfirmDelete = async () => {
    if (!itemToDelete || !deleteActionType) return;

    setActionLoading(true);
    setActionResult(null);
    try {
      let response;
      if (deleteActionType === 'user') {
        response = await adminService.deleteUser(itemToDelete.id);
      } else if (deleteActionType === 'provider') {
        response = await adminService.deleteProvider(itemToDelete.id);
      } else {
        throw new Error('Ismeretlen törlési típus.');
      }

      if (response.success) {
        setActionResult({ type: 'success', message: `${deleteActionType === 'user' ? 'Felhasználó' : 'Szolgáltató'} sikeresen inaktiválva!` });
        loadData(); // Adatok újratöltése
      } else {
        setActionResult({ type: 'error', message: response.message || `${deleteActionType === 'user' ? 'Felhasználó' : 'Szolgáltató'} inaktiválása sikertelen!` });
      }
    } catch (error) {
      setActionResult({ type: 'error', message: error.message || `Hiba történt a ${deleteActionType === 'user' ? 'felhasználó' : 'szolgáltató'} inaktiválása során!` });
    } finally {
      setActionLoading(false);
      setDeleteConfirmationDialogOpen(false);
      setItemToDelete(null);
      setDeleteActionType('');
      setTimeout(() => setActionResult(null), 5000);
    }
  };


  // ÚJ: Felhasználó visszaállítása (reaktiválás)
  const handleReactivateUser = async (userItem) => {
    if (!(await confirm({
      title: 'Felhasználó visszaállítása',
      message: `Visszaállítod ${userItem.firstName} ${userItem.lastName} (${userItem.email}) fiókját? Újra be tud majd jelentkezni.`,
      confirmLabel: 'Visszaállítás',
      color: 'success',
      icon: <Restore />,
    }))) return;
    setActionLoading(true);
    setActionResult(null);
    try {
      const response = await adminService.reactivateUser(userItem.id);
      if (response.success) {
        setActionResult({ type: 'success', message: 'Felhasználó sikeresen visszaállítva!' });
        loadData();
      } else {
        setActionResult({ type: 'error', message: response.message || 'Felhasználó visszaállítása sikertelen!' });
      }
    } catch (error) {
      setActionResult({ type: 'error', message: error.message || 'Hiba történt a felhasználó visszaállítása során!' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setActionResult(null), 5000);
    }
  };

  // ÚJ: Szolgáltató visszaállítása (reaktiválás)
  const handleReactivateProvider = async (provider) => {
    if (!(await confirm({
      title: 'Szolgáltató visszaállítása',
      message: `Visszaállítod a(z) ${provider.businessName} szolgáltatót? A profil jóváhagyott állapotba kerül, és újra megjelenik a keresésben.`,
      confirmLabel: 'Visszaállítás',
      color: 'success',
      icon: <Restore />,
    }))) return;
    setActionLoading(true);
    setActionResult(null);
    try {
      const response = await adminService.reactivateProvider(provider.id);
      if (response.success) {
        setActionResult({ type: 'success', message: 'Szolgáltató sikeresen visszaállítva!' });
        loadData();
      } else {
        setActionResult({ type: 'error', message: response.message || 'Szolgáltató visszaállítása sikertelen!' });
      }
    } catch (error) {
      setActionResult({ type: 'error', message: error.message || 'Hiba történt a szolgáltató visszaállítása során!' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setActionResult(null), 5000);
    }
  };

  const handleViewProvider = (provider) => {
    setSelectedProvider(provider);
    setAdminNotes('');
    setApprovalDialogOpen(true);
  };

  const handleMenuSelect = (newValue) => {
    setSearchParams({ tab: TAB_SLUGS[newValue] });
    setSearchInput('');
    setSearchTerm('');
    setPendingPage(1);
    setApprovedPage(1);
    setRejectedPage(1);
    setUsersPage(1);
    setDeletedPage(1);
    setCommissionGroupsPage(1);
    setCommissionedBookingsPage(1);
    setMobileDrawerOpen(false);
  };

  const resetPages = () => {
    setPendingPage(1);
    setApprovedPage(1);
    setRejectedPage(1);
    setUsersPage(1);
    setDeletedPage(1);
    setCommissionedBookingsPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleSearchSubmit = () => {
    if (searchInput === searchTerm) loadData();
    else { resetPages(); setSearchTerm(searchInput); }
  };

  useEffect(() => {
    if (searchInput === searchTerm) return undefined;
    const timer = setTimeout(() => { resetPages(); setSearchTerm(searchInput); }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a resetPages csak stabil settereket hív
  }, [searchInput, searchTerm]);

  const handleUsersSortChange = (field) => {
    setUsersPage(1);
    if (usersSortBy === field) {
      setUsersSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setUsersSortBy(field);
      setUsersSortOrder('asc');
    }
  };

  const handlePageChange = (event, newPage) => {
    switch (tabValue) {
      case 0: setPendingPage(newPage); break;
      case 1: setApprovedPage(newPage); break;
      case 2: setUsersPage(newPage); break;
      case 3: setCommissionGroupsPage(newPage); break;
      case 4: setCommissionedBookingsPage(newPage); break;
      case 5: /* Blog tabnak nincs lapozása itt */ break;
      case 8: setRejectedPage(newPage); break;
      case 12: setDeletedPage(newPage); break;
      default: break;
    }
  };

  const renderPendingActions = (provider) => (
    <RowActionsMenu
      primaryAction={{
        label: 'Jóváhagy',
        icon: <CheckCircle />,
        variant: 'contained',
        color: 'success',
        disabled: actionLoading,
        onClick: async () => {
          // Hiányos profilnál rákérdezünk — nem tiltjuk, csak figyelmeztetünk
          const c = provider.completeness;
          if (c && !c.ready && !(await confirm({
            title: 'Hiányos profil jóváhagyása',
            message: `${provider.businessName} profilja hiányos (${c.missing.join(', ')}). Biztosan jóváhagyod?`,
            confirmLabel: 'Jóváhagyás',
            color: 'success',
          }))) return;
          handleProviderAction(provider.id, 'approve');
        },
      }}
      menuActions={[
        { label: 'Részletek', icon: <Visibility fontSize="small" />, onClick: () => handleViewProvider(provider) },
        { label: 'Szerkesztés', icon: <Edit fontSize="small" />, onClick: () => navigate(`/admin/providers/edit/${provider.id}`) },
        { label: 'Profil megtekintése', icon: <OpenInNew fontSize="small" />, onClick: () => window.open(`/szolgaltato/${provider.id}`, '_blank') },
        { label: 'Elutasítás indoklással…', icon: <Cancel fontSize="small" />, color: 'error', disabled: actionLoading, onClick: () => handleViewProvider(provider) },
      ]}
    />
  );

  const renderApprovedActions = (provider) => (
    <RowActionsMenu
      primaryAction={{
        icon: <Visibility />,
        color: 'primary',
        iconOnly: true,
        title: 'Profil megtekintése',
        onClick: () => window.open(`/szolgaltato/${provider.id}`, '_blank'),
      }}
      menuActions={[
        { label: 'Szerkesztés', icon: <Edit fontSize="small" />, onClick: () => navigate(`/admin/providers/edit/${provider.id}`) },
        { label: 'Jóváhagyás visszavonása…', icon: <Block fontSize="small" />, onClick: () => handleViewProvider(provider) },
        { label: 'Inaktiválás', icon: <Delete fontSize="small" />, color: 'error', onClick: () => handleDeleteClick(provider, 'provider') },
      ]}
    />
  );

  const renderRejectedActions = (provider) => (
    <RowActionsMenu
      primaryAction={{
        label: 'Jóváhagy',
        icon: <CheckCircle />,
        variant: 'contained',
        color: 'success',
        disabled: actionLoading,
        onClick: async () => {
          if (!(await confirm({
            title: 'Elutasított szolgáltató jóváhagyása',
            message: `Biztosan jóváhagyod a korábban elutasított ${provider.businessName} profilt? Ezzel megjelenik a keresésben.`,
            confirmLabel: 'Jóváhagyás',
            color: 'success',
          }))) return;
          handleProviderAction(provider.id, 'approve');
        },
      }}
      menuActions={[
        { label: 'Részletek', icon: <Visibility fontSize="small" />, onClick: () => handleViewProvider(provider) },
        { label: 'Profil megtekintése', icon: <OpenInNew fontSize="small" />, onClick: () => window.open(`/szolgaltato/${provider.id}`, '_blank') },
      ]}
    />
  );

  const renderUserActions = (userItem) => (
    <RowActionsMenu
      primaryAction={{
        label: 'Szerkesztés',
        icon: <Edit fontSize="small" />,
        color: 'warning',
        disabled: userItem.userType !== 'PROVIDER',
        onClick: () => navigate(`/admin/providers/edit/${userItem.provider?.id}`),
        iconOnly: true,
        title: userItem.userType === 'PROVIDER' ? 'Szolgáltató szerkesztése' : 'Szerkesztés csak szolgáltatóknál érhető el',
      }}
      menuActions={[
        userItem.isActive
          ? { label: 'Inaktiválás', icon: <Delete fontSize="small" />, color: 'error', onClick: () => handleDeleteClick(userItem, 'user') }
          : { label: 'Visszaállítás', icon: <Restore fontSize="small" />, onClick: () => handleReactivateUser(userItem) },
      ]}
    />
  );

  if (loading && !stats) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Admin Dashboard betöltése...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const displayStats = stats || {
    totalUsers: 0,
    totalClients: 0,
    totalProviders: 0,
    pendingProviders: 0,
    totalReviews: 0,
    pendingReviews: 0,
    totalCommissionAmount: 0,
    pendingCommissionPayouts: 0,
  };

  // Csoportosított menü; a `to` mezős elemek külön oldalra visznek (SPA navigációval)
  const menuGroups = [
    {
      title: 'Szolgáltatók',
      items: [
        { label: 'Jóváhagyásra vár', count: displayStats.pendingProviders, index: 0, icon: <Warning fontSize="small" color="warning" /> },
        { label: 'Jóváhagyott', count: displayStats.approvedProviders, index: 1, icon: <CheckCircle fontSize="small" color="success" /> },
        { label: 'Elutasított', count: displayStats.rejectedProviders, index: 8, icon: <Cancel fontSize="small" color="error" /> },
        { label: 'Piszkozatok', index: 11, icon: <PersonAdd fontSize="small" /> },
        { label: 'Szolgáltató import', index: 13, icon: <UploadFile fontSize="small" /> },
        { label: 'Törölt szolgáltatók', count: displayStats.deletedProviders, index: 12, icon: <Restore fontSize="small" color="disabled" /> },
      ],
    },
    {
      title: 'Felhasználók',
      items: [
        { label: 'Felhasználók', count: displayStats.totalUsers, index: 2, icon: <People fontSize="small" color="primary" /> },
        { label: 'Hibabejelentések', index: 9, icon: <BugReportIcon fontSize="small" /> },
      ],
    },
    {
      title: 'Pénzügy',
      items: [
        { label: 'Featured előfizetések', index: 6, icon: <EmojiEvents fontSize="small" /> },
        { label: 'Számlák', index: 14, icon: <Receipt fontSize="small" color="primary" /> },
        { label: 'Jutalékcsoportok', index: 3, icon: <AttachMoney fontSize="small" /> },
        { label: 'Jutalékos foglalások', index: 4, icon: <Receipt fontSize="small" /> },
      ],
    },
    {
      title: 'Tartalom',
      items: [
        { label: 'Blogbejegyzések', index: 5, icon: <Article fontSize="small" /> },
        { label: 'Kategória-tartalmak', index: 16, icon: <Article fontSize="small" /> },
        { label: 'Fórum moderálás', to: '/admin/forum', icon: <Forum fontSize="small" /> },
        { label: 'ÁSZF', index: 10, icon: <Gavel fontSize="small" /> },
      ],
    },
    {
      title: 'Kommunikáció',
      items: [
        { label: 'Tömeges email', index: 7, icon: <Email fontSize="small" /> },
        { label: 'Email sablonok', index: 15, icon: <MarkEmailRead fontSize="small" /> },
      ],
    },
    {
      title: 'Elemzés',
      items: [
        { label: 'Statisztikák', to: '/admin/statisztika', icon: <TrendingUp fontSize="small" /> },
      ],
    },
  ];

  const sidebarContent = (
    <List dense disablePadding sx={{ pb: 1 }}>
      {menuGroups.map((group) => (
        <React.Fragment key={group.title}>
          <ListSubheader
            disableSticky
            sx={{ lineHeight: 1, pt: 2, pb: 0.75, px: 2, bgcolor: 'transparent', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}
          >
            {group.title}
          </ListSubheader>
          {group.items.map((item) => {
            const selected = item.index !== undefined && tabValue === item.index;
            return (
              <ListItemButton
                key={item.label}
                selected={selected}
                onClick={() => {
                  if (item.to) { setMobileDrawerOpen(false); navigate(item.to); } else handleMenuSelect(item.index);
                }}
                sx={{ py: 1, px: 2 }}
              >
                <ListItemIcon sx={{ minWidth: 34 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: selected ? 600 : 400 }}
                />
                {item.count !== undefined && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 600 }}>
                    {item.count ?? 0}
                  </Typography>
                )}
              </ListItemButton>
            );
          })}
        </React.Fragment>
      ))}
    </List>
  );

  const tabTitles = {
    0: 'Jóváhagyásra váró szolgáltatók',
    1: 'Jóváhagyott szolgáltatók',
    2: 'Felhasználók kezelése',
    3: 'Jutalékcsoportok kezelése',
    4: 'Jutalékos foglalások áttekintése',
    5: 'Blogbejegyzések kezelése',
    6: 'Featured előfizetések',
    7: 'Tömeges email küldő',
    8: 'Elutasított szolgáltatók',
    9: 'Hibabejelentések',
    10: 'ÁSZF',
    11: 'Piszkozatok / Meghívandó',
    12: 'Törölt szolgáltatók',
    13: 'Szolgáltató import',
    14: 'Számlák (Billingo)',
    15: 'Email sablonok',
  };

  // A brand arany (secondary.main) ikonként is túl halvány a krém kártyán, ezért itt sötétebb árnyalat
  const statCards = [
    { label: 'Összes felhasználó', value: displayStats.totalUsers, icon: People, color: 'primary.main' },
    { label: 'Kliensek', value: displayStats.totalClients, icon: PersonIcon, color: 'info.main' },
    { label: 'Jóváhagyott szolgáltató', value: displayStats.approvedProviders, icon: Business, color: '#a87a1f' },
    { label: 'Jóváhagyásra vár', value: displayStats.pendingProviders, icon: Warning, color: 'warning.main' },
    { label: 'Összes értékelés', value: displayStats.totalReviews, icon: Star, color: 'success.main' },
    { label: 'Összes jutalék', value: formatHuf(displayStats.totalCommissionAmount, { fallback: '0 Ft' }), icon: AttachMoney, color: 'success.main' },
    { label: 'Függőben lévő jutalék', value: formatHuf(displayStats.pendingCommissionPayouts, { fallback: '0 Ft' }), icon: Receipt, color: 'warning.main' },
  ];

  return (
    <ThemeProvider theme={withReadableWarning}>
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Fejléc */}
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.6rem', md: '2.125rem' } }}>
          Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Üdvözöljük, {user.firstName}! Itt kezelheti a piactér működését.
        </Typography>
      </Box>

      {/* Mobil admin menüsáv: a lap tetején, görgetéskor a fejléc alá tapad */}
      {isMobile && (
        <Paper
          variant="outlined"
          sx={{ position: 'sticky', top: 80, zIndex: (t) => t.zIndex.appBar - 1, mb: 2, borderRadius: 2 }}
        >
          <Button
            fullWidth
            onClick={() => setMobileDrawerOpen(true)}
            startIcon={<MenuIcon />}
            endIcon={<ExpandMore />}
            sx={{ justifyContent: 'flex-start', py: 1.25, px: 2, textAlign: 'left', '& .MuiButton-endIcon': { ml: 'auto' } }}
          >
            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Typography component="span" variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                Admin menü
              </Typography>
              <Typography component="span" variant="subtitle1" fontWeight={600} noWrap sx={{ lineHeight: 1.3 }}>
                {tabTitles[tabValue]}
              </Typography>
            </Box>
          </Button>
        </Paper>
      )}

      {/* Akció eredmény */}
      {actionResult && (
        <Alert
          severity={actionResult.type}
          sx={{ mb: 3 }}
          onClose={() => setActionResult(null)}
        >
          {actionResult.message}
        </Alert>
      )}

      {/* Statisztikák */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(7, 1fr)' },
          gap: { xs: 1.5, md: 2 },
          mb: { xs: 3, md: 4 },
        }}
      >
        {statCards.map(({ label, value, icon: Icon, color }) => (
          <Card key={label} sx={{ minWidth: 0 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
              <Icon sx={{ color, fontSize: { xs: 28, md: 36 }, mb: 0.5 }} />
              {/* A szám mindig a sötét szövegszínnel: az arany/narancs/kék a krém háttéren nem olvasható elég jól */}
              <Typography variant="h5" color="text.primary" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, overflowWrap: 'anywhere' }}>
                {value}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                {label}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Drawer
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        variant="temporary"
        // A fix fejléc zIndex-e drawer+1 — fölé kell kerülnünk, különben eltakarja a menü tetejét
        sx={{ zIndex: (t) => t.zIndex.drawer + 2, '& .MuiDrawer-paper': { width: 280, maxWidth: '85vw' } }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700}>Admin menü</Typography>
          <IconButton onClick={() => setMobileDrawerOpen(false)} aria-label="Menü bezárása">
            <Close />
          </IconButton>
        </Box>
        <Divider />
        {sidebarContent}
      </Drawer>

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mb: 3 }}>
        {!isMobile && (
          <Paper
            variant="outlined"
            sx={{
              width: 240, flexShrink: 0, borderRadius: 2, overflowY: 'auto',
              position: 'sticky', top: 96, maxHeight: 'calc(100vh - 112px)',
            }}
          >
            {sidebarContent}
          </Paper>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                {!isMobile && (
                  <Typography variant="h6">
                    {tabTitles[tabValue]}
                  </Typography>
                )}
                {(tabValue === 0 || tabValue === 1 || tabValue === 2 || tabValue === 4 || tabValue === 12) && (
                  <TextField
                    size="small"
                    placeholder="Keresés..."
                    value={searchInput}
                    onChange={handleSearchChange}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Box>

          {refreshing && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && tabValue === 0 && ( // Jóváhagyásra váró szolgáltatók
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">Profil állapota:</Typography>
                {[
                  { value: 'all', label: 'Mind' },
                  { value: 'ready', label: 'Kész profilok' },
                  { value: 'incomplete', label: 'Hiányos profilok' },
                ].map(opt => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    size="small"
                    color={pendingReadiness === opt.value ? 'primary' : 'default'}
                    variant={pendingReadiness === opt.value ? 'filled' : 'outlined'}
                    onClick={() => { setPendingReadiness(opt.value); setPendingPage(1); }}
                  />
                ))}
              </Box>
              {pendingProviders.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                  Nincsenek függőben lévő szolgáltatók.
                </Paper>
              ) : isNarrow ? (
                <Box>
                  {pendingProviders.map((provider) => (
                    <MobileEntityCard
                      key={provider.id}
                      avatar={<Avatar sx={{ bgcolor: 'primary.main' }}>{provider.businessName?.charAt(0) || provider.user?.firstName?.charAt(0)}</Avatar>}
                      title={provider.businessName}
                      subtitle={(
                        <>
                          <Typography variant="body2" color="text.secondary">{provider.user?.firstName} {provider.user?.lastName}</Typography>
                          <Typography variant="caption" color="text.secondary">{provider.user?.email}</Typography>
                        </>
                      )}
                      fields={[
                        { label: 'Kategória', value: <Chip label={provider.category?.name || 'N/A'} size="small" /> },
                        { label: 'Helyszín', value: provider.city },
                        { label: 'Profil', value: <ProviderCompletenessChip completeness={provider.completeness} /> },
                        { label: 'Regisztráció', value: new Date(provider.createdAt).toLocaleDateString('hu-HU') },
                      ]}
                      actions={renderPendingActions(provider)}
                    />
                  ))}
                </Box>
              ) : (
                <ResponsiveTableContainer stickyLastColumn>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Szolgáltató</TableCell>
                        <TableCell>Kategória</TableCell>
                        <TableCell sx={hideBelowXl}>Helyszín</TableCell>
                        <TableCell>Profil</TableCell>
                        <TableCell sx={hideBelowLg}>Regisztráció</TableCell>
                        <TableCell>Műveletek</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingProviders.map((provider) => (
                        <TableRow key={provider.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                {provider.businessName?.charAt(0) || provider.user?.firstName?.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2">
                                  {provider.businessName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {provider.user?.firstName} {provider.user?.lastName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {provider.user?.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={provider.category?.name || 'N/A'} size="small" />
                          </TableCell>
                          <TableCell sx={hideBelowXl}>{provider.city}</TableCell>
                          <TableCell>
                            <ProviderCompletenessChip completeness={provider.completeness} />
                            {provider.providerConfirmedAt && (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                Meghívóból, beküldve: {new Date(provider.providerConfirmedAt).toLocaleDateString('hu-HU')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ ...hideBelowLg, ...noWrap }}>{new Date(provider.createdAt).toLocaleDateString('hu-HU')}</TableCell>
                          <TableCell>{renderPendingActions(provider)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ResponsiveTableContainer>
              )}
              {pendingPagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, p: 2 }}>
                  <Pagination
                    count={pendingPagination.totalPages}
                    page={pendingPage}
                    onChange={(e, p) => setPendingPage(p)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}

          {!loading && tabValue === 1 && ( // Jóváhagyott szolgáltatók
            <>
              {approvedProviders.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                  Nincsenek jóváhagyott szolgáltatók.
                </Paper>
              ) : isNarrow ? (
                <Box>
                  {approvedProviders.map((provider) => (
                    <MobileEntityCard
                      key={provider.id}
                      avatar={<Avatar sx={{ bgcolor: 'secondary.main' }}>{provider.businessName?.charAt(0) || provider.user?.firstName?.charAt(0)}</Avatar>}
                      title={provider.businessName}
                      subtitle={(
                        <Typography variant="body2" color="text.secondary">{provider.user?.firstName} {provider.user?.lastName}</Typography>
                      )}
                      fields={[
                        { label: 'Kategória', value: <Chip label={provider.category?.name || 'N/A'} size="small" color="primary" /> },
                        { label: 'Értékelés', value: `${provider.averageRating?.toFixed(1) || 'N/A'} (${provider.reviewCount || 0})` },
                        { label: 'Jutalékcsoport', value: <Chip label={provider.commissionGroup?.name || 'Alapértelmezett'} size="small" color={provider.commissionGroup ? 'info' : 'default'} /> },
                        { label: 'Jóváhagyás dátuma', value: new Date(provider.updatedAt).toLocaleDateString('hu-HU') },
                      ]}
                      actions={renderApprovedActions(provider)}
                    />
                  ))}
                </Box>
              ) : (
                <ResponsiveTableContainer stickyLastColumn>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Szolgáltató</TableCell>
                        <TableCell>Kategória</TableCell>
                        <TableCell sx={hideBelowLg}>Értékelés</TableCell>
                        <TableCell>Jutalékcsoport</TableCell>
                        <TableCell sx={hideBelowLg}>Jóváhagyás dátuma</TableCell>
                        <TableCell>Műveletek</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {approvedProviders.map((provider) => (
                        <TableRow key={provider.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ mr: 2, bgcolor: 'secondary.main' }}>
                                {provider.businessName?.charAt(0) || provider.user?.firstName?.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2">
                                  {provider.businessName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {provider.user?.firstName} {provider.user?.lastName}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={provider.category?.name || 'N/A'} size="small" color="primary" />
                          </TableCell>
                          <TableCell sx={hideBelowLg}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ mr: 1 }}>
                                {provider.averageRating?.toFixed(1) || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ({provider.reviewCount || 0} értékelés)
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={provider.commissionGroup?.name || 'Alapértelmezett'}
                              size="small"
                              color={provider.commissionGroup ? 'info' : 'default'}
                            />
                          </TableCell>
                          <TableCell sx={{ ...hideBelowLg, ...noWrap }}>{new Date(provider.updatedAt).toLocaleDateString('hu-HU')}</TableCell>
                          <TableCell>{renderApprovedActions(provider)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ResponsiveTableContainer>
              )}
              {approvedPagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, p: 2 }}>
                  <Pagination
                    count={approvedPagination.totalPages}
                    page={approvedPage}
                    onChange={(e, p) => setApprovedPage(p)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}

          {!loading && tabValue === 2 && ( // Felhasználók
            <>
              {users.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                  Nincsenek felhasználók.
                </Paper>
              ) : isNarrow ? (
                <Box>
                  {users.map((userItem) => (
                    <MobileEntityCard
                      key={userItem.id}
                      avatar={<Avatar sx={{ bgcolor: 'info.main' }}>{userItem.firstName?.charAt(0)}</Avatar>}
                      title={`${userItem.firstName} ${userItem.lastName}`}
                      subtitle={(
                        <>
                          <Typography variant="body2" color="text.secondary">{userItem.email}</Typography>
                          {userItem.provider?.businessName && (
                            <Typography variant="caption" color="text.secondary">({userItem.provider.businessName})</Typography>
                          )}
                        </>
                      )}
                      fields={[
                        { label: 'Típus', value: <Chip label={USER_TYPE_LABELS[userItem.userType] || userItem.userType} size="small" color={userItem.userType === 'ADMIN' ? 'error' : userItem.userType === 'PROVIDER' ? 'secondary' : 'primary'} /> },
                        { label: 'Állapot', value: <Chip label={userItem.isActive ? 'Aktív' : 'Inaktív'} size="small" color={userItem.isActive ? 'success' : 'default'} /> },
                        { label: 'Regisztráció', value: new Date(userItem.createdAt).toLocaleDateString('hu-HU') },
                        { label: 'Utoljára belépett', value: userItem.lastLoginAt ? new Date(userItem.lastLoginAt).toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' }) : 'Még nem' },
                      ]}
                      actions={renderUserActions(userItem)}
                    />
                  ))}
                </Box>
              ) : (
                <ResponsiveTableContainer stickyLastColumn>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={usersSortBy === 'firstName'}
                            direction={usersSortBy === 'firstName' ? usersSortOrder : 'asc'}
                            onClick={() => handleUsersSortChange('firstName')}
                          >
                            Felhasználó
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={hideBelowXl}>
                          <TableSortLabel
                            active={usersSortBy === 'email'}
                            direction={usersSortBy === 'email' ? usersSortOrder : 'asc'}
                            onClick={() => handleUsersSortChange('email')}
                          >
                            Email
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={hideBelowLg}>Típus</TableCell>
                        <TableCell>Állapot</TableCell>
                        <TableCell sx={hideBelowLg}>
                          <TableSortLabel
                            active={usersSortBy === 'createdAt'}
                            direction={usersSortBy === 'createdAt' ? usersSortOrder : 'asc'}
                            onClick={() => handleUsersSortChange('createdAt')}
                          >
                            Regisztráció
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={usersSortBy === 'lastLoginAt'}
                            direction={usersSortBy === 'lastLoginAt' ? usersSortOrder : 'asc'}
                            onClick={() => handleUsersSortChange('lastLoginAt')}
                          >
                            Utoljára belépett
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Műveletek</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((userItem) => (
                        <TableRow key={userItem.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ mr: 2, bgcolor: 'info.main' }}>
                                {userItem.firstName?.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2">
                                  {userItem.firstName} {userItem.lastName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'block', xl: 'none' } }}>
                                  {userItem.email}
                                </Typography>
                                {userItem.provider?.businessName && (
                                  <Typography variant="caption" color="text.secondary">
                                    ({userItem.provider.businessName})
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={hideBelowXl}>{userItem.email}</TableCell>
                          <TableCell sx={hideBelowLg}>
                            <Chip
                              label={USER_TYPE_LABELS[userItem.userType] || userItem.userType}
                              size="small"
                              color={userItem.userType === 'ADMIN' ? 'error' : userItem.userType === 'PROVIDER' ? 'secondary' : 'primary'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={userItem.isActive ? 'Aktív' : 'Inaktív'}
                              size="small"
                              color={userItem.isActive ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell sx={{ ...hideBelowLg, ...noWrap }}>{new Date(userItem.createdAt).toLocaleDateString('hu-HU')}</TableCell>
                          <TableCell sx={noWrap}>
                            {userItem.lastLoginAt ? new Date(userItem.lastLoginAt).toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' }) : 'Még nem'}
                          </TableCell>
                          <TableCell>{renderUserActions(userItem)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ResponsiveTableContainer>
              )}
              {usersPagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, p: 2 }}>
                  <Pagination
                    count={usersPagination.totalPages}
                    page={usersPage}
                    onChange={(e, p) => setUsersPage(p)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}

          {!loading && tabValue === 12 && ( // ÚJ: Törölt (inaktivált) szolgáltatók
            <>
              {deletedProviders.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                  Nincsenek törölt szolgáltatók.
                </Paper>
              ) : isNarrow ? (
                <Box>
                  {deletedProviders.map((provider) => (
                    <MobileEntityCard
                      key={provider.id}
                      avatar={<Avatar sx={{ bgcolor: 'grey.500' }}>{provider.businessName?.charAt(0) || provider.user?.firstName?.charAt(0)}</Avatar>}
                      title={provider.businessName}
                      subtitle={(
                        <Typography variant="body2" color="text.secondary">{provider.user?.firstName} {provider.user?.lastName}</Typography>
                      )}
                      fields={[
                        { label: 'Kategória', value: <Chip label={provider.category?.name || 'N/A'} size="small" color="primary" /> },
                        { label: 'Törlés dátuma', value: provider.deletedAt ? new Date(provider.deletedAt).toLocaleDateString('hu-HU') : '-' },
                        { label: 'Törölte', value: provider.deletedBy ? (provider.deletedByUser ? `${provider.deletedByUser.firstName} ${provider.deletedByUser.lastName} (admin)` : `Admin (#${provider.deletedBy})`) : 'Önkiszolgáló' },
                      ]}
                      actions={(
                        <Button size="small" variant="outlined" color="success" startIcon={<Restore />} onClick={() => handleReactivateProvider(provider)} disabled={actionLoading}>
                          Visszaállítás
                        </Button>
                      )}
                    />
                  ))}
                </Box>
              ) : (
                <ResponsiveTableContainer stickyLastColumn>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Szolgáltató</TableCell>
                        <TableCell>Kategória</TableCell>
                        <TableCell>Törlés dátuma</TableCell>
                        <TableCell>Törölte</TableCell>
                        <TableCell>Műveletek</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deletedProviders.map((provider) => (
                        <TableRow key={provider.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ mr: 2, bgcolor: 'grey.500' }}>
                                {provider.businessName?.charAt(0) || provider.user?.firstName?.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2">
                                  {provider.businessName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {provider.user?.firstName} {provider.user?.lastName}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={provider.category?.name || 'N/A'} size="small" color="primary" />
                          </TableCell>
                          <TableCell>{provider.deletedAt ? new Date(provider.deletedAt).toLocaleDateString('hu-HU') : '-'}</TableCell>
                          <TableCell>{provider.deletedBy ? (provider.deletedByUser ? `${provider.deletedByUser.firstName} ${provider.deletedByUser.lastName} (admin)` : `Admin (#${provider.deletedBy})`) : 'Önkiszolgáló'}</TableCell>
                          <TableCell>
                            <Tooltip title="Visszaállítás">
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  aria-label="Visszaállítás"
                                  disabled={actionLoading}
                                  onClick={() => handleReactivateProvider(provider)}
                                >
                                  <Restore />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ResponsiveTableContainer>
              )}
              {deletedPagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, p: 2 }}>
                  <Pagination
                    count={deletedPagination.totalPages}
                    page={deletedPage}
                    onChange={(e, p) => setDeletedPage(p)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}

          {tabValue === 14 && (
            <AdminInvoicesPage />
          )}

          {!loading && tabValue === 6 && ( // ÚJ: Featured tab tartalma
            <AdminFeaturedPage />
          )}

          {!loading && tabValue === 3 && ( // Jutalékcsoportok tab tartalma
            <CommissionGroupsPage />
          )}

          {!loading && tabValue === 4 && ( // Jutalékos foglalások tab tartalma
            <CommissionedBookingsPage
              page={commissionedBookingsPage}
              limit={10}
              searchTerm={searchTerm}
              onPageChange={(e, p) => setCommissionedBookingsPage(p)}
              pagination={commissionedBookingsPagination}
            />
          )}

          {!loading && tabValue === 5 && (
            <AdminBlogPostsPage />
          )}

          {tabValue === 7 && (
            <AdminBulkEmailPage />
          )}

          {tabValue === 9 && (
            <BugReportsPage />
          )}

          {tabValue === 16 && (
            <AdminCategoryContentPage />
          )}

          {tabValue === 10 && (
            <AdminLegalPage />
          )}

          {tabValue === 11 && (
            <AdminDraftProvidersPage />
          )}

          {tabValue === 13 && (
            <AdminProviderImportPage />
          )}

          {tabValue === 15 && (
            <AdminEmailTemplatesPage />
          )}

          {!loading && tabValue === 8 && ( // Elutasított szolgáltatók
            <>
              {rejectedProviders.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                  Nincsenek elutasított szolgáltatók.
                </Paper>
              ) : isNarrow ? (
                <Box>
                  {rejectedProviders.map((provider) => (
                    <MobileEntityCard
                      key={provider.id}
                      avatar={<Avatar sx={{ bgcolor: 'error.main' }}>{provider.businessName?.charAt(0) || provider.user?.firstName?.charAt(0)}</Avatar>}
                      title={provider.businessName}
                      subtitle={(
                        <>
                          <Typography variant="body2" color="text.secondary">{provider.user?.firstName} {provider.user?.lastName}</Typography>
                          <Typography variant="caption" color="text.secondary">{provider.user?.email}</Typography>
                        </>
                      )}
                      fields={[
                        { label: 'Kategória', value: <Chip label={provider.category?.name || 'N/A'} size="small" /> },
                        { label: 'Helyszín', value: provider.city },
                        { label: 'Elutasítás dátuma', value: new Date(provider.updatedAt).toLocaleDateString('hu-HU') },
                      ]}
                      actions={renderRejectedActions(provider)}
                    />
                  ))}
                </Box>
              ) : (
                <ResponsiveTableContainer stickyLastColumn>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Szolgáltató</TableCell>
                        <TableCell>Kategória</TableCell>
                        <TableCell sx={hideBelowLg}>Helyszín</TableCell>
                        <TableCell>Elutasítás dátuma</TableCell>
                        <TableCell>Műveletek</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rejectedProviders.map((provider) => (
                        <TableRow key={provider.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ mr: 2, bgcolor: 'error.main' }}>
                                {provider.businessName?.charAt(0) || provider.user?.firstName?.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2">
                                  {provider.businessName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {provider.user?.firstName} {provider.user?.lastName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {provider.user?.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={provider.category?.name || 'N/A'} size="small" />
                          </TableCell>
                          <TableCell sx={hideBelowLg}>{provider.city}</TableCell>
                          <TableCell sx={noWrap}>{new Date(provider.updatedAt).toLocaleDateString('hu-HU')}</TableCell>
                          <TableCell>{renderRejectedActions(provider)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ResponsiveTableContainer>
              )}
              {rejectedPagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, p: 2 }}>
                  <Pagination
                    count={rejectedPagination.totalPages}
                    page={rejectedPage}
                    onChange={(e, p) => setRejectedPage(p)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
            </Box>
          </Paper>
        </Box>
      </Box>
      {/* Szolgáltató részletek dialog */}
      <Dialog
        open={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedProvider && (
          <>
            <DialogTitle sx={{ p: 0 }}>
              <DialogHeader
                icon={<Business />}
                title="Szolgáltató részletei"
                subtitle={selectedProvider.businessName}
              />
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Kapcsolattartó neve:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedProvider.user?.firstName} {selectedProvider.user?.lastName}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Email cím:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedProvider.user?.email}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Telefonszám:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedProvider.user?.phone}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Kategória:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedProvider.category?.name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Bemutatkozás:
                  </Typography>
                  <Typography variant="body2">
                    {selectedProvider.description}
                  </Typography>
                </Grid>
                {selectedProvider.completeness && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Ellenőrzési szempontok: <ProviderCompletenessChip completeness={selectedProvider.completeness} />
                    </Typography>
                    <ProviderCompletenessChecklist completeness={selectedProvider.completeness} />
                  </Grid>
                )}
              </Grid>
              {(selectedProvider.status === 'PENDING' || selectedProvider.status === 'APPROVED') && (
                <TextField
                  autoFocus
                  margin="dense"
                  label={selectedProvider.status === 'APPROVED' ? 'Admin megjegyzés (visszavonás oka)' : 'Admin megjegyzés (elutasítás esetén)'}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  sx={{ mt: 3 }}
                />
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
              <Button onClick={() => setApprovalDialogOpen(false)} disabled={actionLoading}>
                Bezárás
              </Button>
              <Button
                variant="outlined"
                startIcon={<OpenInNew />}
                onClick={() => window.open(`/szolgaltato/${selectedProvider.id}`, '_blank')}
              >
                Profil megtekintése
              </Button>
              {selectedProvider.status === 'PENDING' && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={actionLoading ? <CircularProgress size={20} /> : <Cancel />}
                  onClick={() => handleProviderAction(selectedProvider.id, 'reject')}
                  disabled={actionLoading}
                >
                  Elutasítás
                </Button>
              )}
              {selectedProvider.status === 'APPROVED' && (
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={actionLoading ? <CircularProgress size={20} /> : <Block />}
                  onClick={() => handleProviderAction(selectedProvider.id, 'revoke')}
                  disabled={actionLoading}
                >
                  Jóváhagyás visszavonása
                </Button>
              )}
              {(selectedProvider.status === 'PENDING' || selectedProvider.status === 'REJECTED') && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={actionLoading ? <CircularProgress size={20} /> : <CheckCircle />}
                  onClick={() => handleProviderAction(selectedProvider.id, 'approve')}
                  disabled={actionLoading}
                >
                  Jóváhagyás
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Törlés megerősítő dialógus (általános felhasználóra és szolgáltatóra) */}
      <Dialog
        open={deleteConfirmationDialogOpen}
        onClose={() => setDeleteConfirmationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={<Delete />}
            title={deleteActionType === 'user' ? 'Felhasználó inaktiválása' : 'Szolgáltató inaktiválása'}
          />
        </DialogTitle>
        <DialogContent>
          {itemToDelete && (
            <Typography>
              Biztosan inaktiválja a(z){' '}
              <strong>
                {deleteActionType === 'user'
                  ? `${itemToDelete.firstName} ${itemToDelete.lastName}`
                  : itemToDelete.businessName}
              </strong>{' '}
              {deleteActionType === 'user' ? 'felhasználót' : 'szolgáltatót'}?
            </Typography>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            A(z) {deleteActionType === 'user' ? 'felhasználó' : 'szolgáltató'} inaktiválásra
            kerül, és nem lesz elérhető/bejelentkezhető. A meglévő foglalások, üzenetek és
            értékelések megmaradnak, az adatok nem törlődnek. Később a
            {deleteActionType === 'user' ? ' Felhasználók' : ' Törölt szolgáltatók'} listában visszaállítható.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setDeleteConfirmationDialogOpen(false)} disabled={actionLoading}>
            Mégse
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <Delete />}
          >
            Inaktiválás
          </Button>
        </DialogActions>
      </Dialog>
      {confirmDialog}
    </Container>
    </ThemeProvider>
  );
};

export default AdminDashboard;